import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { fillArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { ExploreItem } from 'tripPlanner/types/common'
import ThingsToDoPlaceTile from './ThingsToDoPlaceTile'
import ThingsToDoPlaceTileSkeleton from './ThingsToDoPlaceTileSkeleton'
import thingsToDoNotices from './thingsToDoNotices'
import { getDisplayRegion } from 'tripPlanner/utils/explorePlaces'

const DEFAULT_SKELETON_COUNT = 3

interface Props {
  places: Array<ExploreItem>
  isLoading: boolean
  /** A source failed, so an empty list is incomplete rather than a genuine no-results. */
  isPartial?: boolean
  skeletonCount?: number
  destinationName: string
  categoryLabel: string
  onSelectPlace: (place: ExploreItem) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAction?: () => void
}

function ThingsToDoPlacesResults({
  places,
  isLoading,
  isPartial,
  skeletonCount,
  destinationName,
  categoryLabel,
  onSelectPlace,
  itineraryData,
  onSendMessage,
  onAfterAction = noop,
}: Props) {
  const intl = useIntl()
  const skeletonKeys = useMemo(
    () => fillArray(skeletonCount ?? DEFAULT_SKELETON_COUNT, (index) => `things-to-do-skeleton-${index}`),
    [skeletonCount],
  )

  if (isLoading) {
    return (
      <Group direction="vertical" gap={16}>
        {skeletonKeys.map((key) => (
          <ThingsToDoPlaceTileSkeleton key={key} />
        ))}
      </Group>
    )
  }

  if (places.length === 0 && !isPartial) {
    return (
      <Group direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
        <Heading variant="heading5">
          {intl.formatMessage(thingsToDoNotices.emptyHeading, { category: categoryLabel, destination: destinationName })}
        </Heading>
        <BodyText variant="medium" colour="light-foreground-alt" align="center">
          {intl.formatMessage(thingsToDoNotices.emptyBody)}
        </BodyText>
      </Group>
    )
  }

  return (
    <Group direction="vertical" gap={16}>
      {places.map((place) => (
        <ThingsToDoPlaceTile
          key={place.id}
          place={place}
          displayRegion={getDisplayRegion(place.place.region, destinationName)}
          onSelectPlace={onSelectPlace}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
          onAfterAction={onAfterAction}
        />
      ))}
    </Group>
  )
}

export default ThingsToDoPlacesResults
